import { Head, usePage } from "@inertiajs/react"
import { format } from "date-fns"

import { PageHeader } from "@/components/console/page-header"
import { Checkbox } from "@/components/ui/checkbox"
import { Notice } from "@/components/ui/notice"
import { AdminLayout } from "@/layouts/admin-layout"

import { type EmailMessageSummary, type WebhookEvent } from "../messages/types"

import { AddressCheck } from "./_components/address-check"
import { Allowlist } from "./_components/allowlist"
import { ExternalChip } from "./_components/checks"
import { DomainHealth } from "./_components/domain-health"
import { MovementStats, Suppressions } from "./_components/movement"
import { RecentEvents, RecentLedger } from "./_components/recent-tables"
import { Reference } from "./_components/reference"
import { StatusStrip } from "./_components/status-strip"
import { useAutoRefresh } from "./_components/use-auto-refresh"
import { deriveSectionStatuses } from "./status"
import type { DryRunResult, HealthReport, Links, Triggers } from "./types"

interface Props {
  report: HealthReport
  ledger_messages: EmailMessageSummary[]
  recent_events: WebhookEvent[]
  triggers: Triggers
  dry_run: DryRunResult | null
  links: Links
}

export default function AdminEmailHealth({
  report,
  ledger_messages,
  recent_events,
  triggers,
  dry_run,
  links,
}: Props) {
  const { errors } = usePage().props
  const baseError = (
    errors as unknown as Record<string, string | undefined> | undefined
  )?.base

  const { transport, allowlist, deliverability, ledger, webhooks } = report
  const stuck = ledger.awaiting_webhook
  const autoRefresh = useAutoRefresh()

  return (
    <AdminLayout>
      <Head title="Email health" />
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 lg:px-8">
        <PageHeader
          crumbs={[{ label: "email" }, { label: "health" }]}
          title="Email health"
        />

        <StatusStrip statuses={deriveSectionStatuses(report)} />

        {baseError && (
          <Notice tone="error" role="alert">
            {baseError}
          </Notice>
        )}

        {!transport.matches_matrix && (
          <Notice tone="error">
            <strong>Transport mismatch.</strong> The environment matrix selects{" "}
            <code>{transport.provider}</code> for {transport.rails_env}, but
            ActionMailer is set to <code>{transport.delivery_method}</code>.
            Something reassigned <code>delivery_method</code> after
            config/initializers/email.rb ran. Check the environment file. If the
            value is not one ActionMailer registers, every delivery raises.
          </Notice>
        )}

        {webhooks.never_received && (
          <Notice tone="warning">
            <strong>No webhook has ever arrived</strong>, and {stuck.count} live
            row(s) are waiting on one. The endpoint is the suspect, not the
            pipeline: check the webhook URL on the Postmark server, and that the
            basic-auth credentials configured on it match{" "}
            <code>postmark.webhook_username</code> /{" "}
            <code>webhook_password</code> below.
          </Notice>
        )}

        {/* Getting from a failed send to the right log stream in one click
            is most of what this page is for. */}
        <nav
          aria-label="External tools"
          className="flex flex-wrap items-center gap-2"
        >
          <span className="eyebrow">Go to</span>
          <ExternalChip href={links.postmark_activity}>
            Postmark activity
          </ExternalChip>
          <ExternalChip href={links.mailtrap_inbox}>
            Mailtrap sandbox
          </ExternalChip>
          {links.good_job && (
            <ExternalChip href={links.good_job}>GoodJob</ExternalChip>
          )}
          <ExternalChip href={links.render_worker_logs}>
            Worker logs
          </ExternalChip>
          <ExternalChip href={links.render_web_logs}>Web logs</ExternalChip>
          <ExternalChip href={links.render_worker}>Render worker</ExternalChip>
          <ExternalChip href={links.render_web}>Render web</ExternalChip>
          <span className="ml-auto flex items-center gap-2">
            {autoRefresh.refreshing && (
              <span
                role="status"
                aria-label="Refreshing"
                className="remote-dot"
              />
            )}
            {autoRefresh.lastRefreshedAt && (
              <span className="notation">
                Updated {format(autoRefresh.lastRefreshedAt, "HH:mm:ss")}
              </span>
            )}
            <Checkbox
              size="sm"
              label="Auto-refresh · 30 s"
              isSelected={autoRefresh.enabled}
              onChange={autoRefresh.setEnabled}
            />
          </span>
        </nav>

        <AddressCheck
          dryRun={dry_run}
          triggers={triggers}
          webhooksExpected={webhooks.expected}
        />

        <MovementStats report={report} links={links} />

        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
          <Allowlist allowlist={allowlist} transport={transport} />
          <Suppressions suppressions={report.suppressions} />
        </div>

        <RecentLedger messages={ledger_messages} ledgerHref={links.ledger} />

        <RecentEvents events={recent_events} webhooks={webhooks} />

        <DomainHealth deliverability={deliverability} />

        <Reference
          transport={transport}
          credentials={report.credentials}
          links={links}
        />
      </div>
    </AdminLayout>
  )
}
